/**
 * BubbleRenderer — BD-style (bande dessinée) comic speech bubbles.
 * Supports 4 bubble templates (speech/thought/shout/whisper) with
 * customizable colors and position. Uses shared drawing from BubbleTemplates.
 *
 *   - Character name header in accent color
 *   - Template-driven body shape and tail style
 *   - Typewriter text effect
 *   - Depth 550+ (above tutorial overlay range)
 */
import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { TECH } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { t } from '@/i18n/I18n';
import { GOD } from '@/config/GodMode';
import {
  BUBBLE_TEMPLATES, drawBubbleBody, drawBubbleTail, computeTailOrigin,
  type BubbleTemplateConfig,
} from './BubbleTemplates';
import type { BubbleTemplate } from './CinematicTypes';

const DEPTH_BUBBLE = 555;
const BUBBLE_W = 380;
const BUBBLE_PAD = 16;
const TYPEWRITER_CHAR_MS = 25;

const EMOTION_COLORS: Record<string, number> = {
  alert:   0xff4444,
  calm:    TECH.ACCENT,
  excited: TECH.SUCCESS,
};

export interface BubbleConfig {
  speaker: string;
  textKey: string;
  position?: 'top' | 'bottom' | 'center';
  emotion?: 'alert' | 'calm' | 'excited';
  autoDismissMs?: number;
  worldAnchor?: { x: number; y: number };
  portrait?: string;
  /** How to display the portrait. Default: 'inBubble' */
  portraitDisplay?: 'inBubble' | 'bottomLeft' | 'bottomRight';
  /** Standing portrait size as % of screen height (0.2–0.8). Default: 0.4 */
  portraitScale?: number;
  /** Portrait offset from default position */
  portraitOffset?: { x: number; y: number };
  /** Entry transition. Default: 'slide' */
  portraitTransition?: 'slide' | 'fade' | 'pop' | 'none';
  /** Flip portrait horizontally */
  portraitFlip?: boolean;
  /** Show a smartphone frame as prop */
  portraitFrame?: boolean;
  /** Smartphone frame scale */
  frameScale?: number;
  /** Smartphone frame offset relative to portrait */
  frameOffset?: { x: number; y: number };
  // BD customization
  bubbleTemplate?: BubbleTemplate;
  bubblePos?: { x: number; y: number };
  tailTip?: { x: number; y: number };
  borderColor?: number;
  fillColor?: number;
  textColor?: number;
}

export class BubbleRenderer {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private gfx: Phaser.GameObjects.Graphics | null = null;
  private speakerText: Phaser.GameObjects.Text | null = null;
  private bodyText: Phaser.GameObjects.Text | null = null;
  private typewriterTimer: Phaser.Time.TimerEvent | null = null;
  private autoDismissTimer: Phaser.Time.TimerEvent | null = null;
  private standingPortrait: Phaser.GameObjects.Container | null = null;
  private tailGfx: Phaser.GameObjects.Graphics | null = null;
  private entryTween: Phaser.Tweens.Tween | null = null;
  private dismissTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  show(config: BubbleConfig): void {
    this.dismiss(true);

    // ── Resolve template + colors ──
    const tmpl = BUBBLE_TEMPLATES[config.bubbleTemplate ?? 'trueBD'];
    const emotion = config.emotion ?? 'calm';
    const borderColor = config.borderColor ?? EMOTION_COLORS[emotion] ?? tmpl.defaultBorderColor;
    const fillColor = config.fillColor ?? tmpl.defaultFillColor;
    const textColor = config.textColor ?? tmpl.defaultTextColor;
    const fullText = t(config.textKey);
    const speakerName = t(config.speaker);
    const pos = config.position ?? 'bottom';

    const borderHex = '#' + borderColor.toString(16).padStart(6, '0');
    const textHex = '#' + textColor.toString(16).padStart(6, '0');

    // ── Portrait mode ──
    const hasPortrait = config.portrait && this.scene.textures.exists(config.portrait);
    const portraitMode = config.portraitDisplay ?? 'inBubble';
    const inBubblePortrait = hasPortrait && portraitMode === 'inBubble';
    const standingPortraitSide = hasPortrait && portraitMode !== 'inBubble' ? portraitMode : null;
    const portraitSize = 64;
    const portraitGap = 12;
    const textOffsetX = inBubblePortrait ? BUBBLE_PAD + portraitSize + portraitGap : BUBBLE_PAD;
    const bubbleW = inBubblePortrait ? BUBBLE_W + portraitSize + portraitGap : BUBBLE_W;
    const textWrapW = bubbleW - textOffsetX - BUBBLE_PAD;

    // ── Create container ──
    this.container = this.scene.add.container(0, 0).setDepth(DEPTH_BUBBLE);

    // ── Speaker text ──
    this.speakerText = this.scene.add.text(textOffsetX, BUBBLE_PAD, speakerName, {
      fontSize: tmpl.fontSize, color: borderHex,
      fontFamily: 'monospace', fontStyle: 'bold',
    });

    // ── Body text (empty for typewriter) ──
    this.bodyText = this.scene.add.text(textOffsetX, BUBBLE_PAD + 24, '', {
      fontSize: tmpl.fontSize, color: textHex, fontFamily: 'monospace',
      fontStyle: tmpl.fontStyle,
      wordWrap: { width: textWrapW }, lineSpacing: 4,
    });

    // Pre-measure full text height
    const measurer = this.scene.add.text(0, -9999, fullText, {
      fontSize: tmpl.fontSize, fontFamily: 'monospace', fontStyle: tmpl.fontStyle,
      wordWrap: { width: textWrapW }, lineSpacing: 4,
    });
    const textH = measurer.height;
    measurer.destroy();

    const minH = inBubblePortrait ? portraitSize + BUBBLE_PAD * 2 : 0;
    const bubbleH = Math.max(BUBBLE_PAD + 24 + textH + BUBBLE_PAD, minH);

    // ── Background (BD-style body) ──
    this.gfx = this.scene.add.graphics();
    drawBubbleBody(this.gfx, 0, 0, bubbleW, bubbleH, tmpl,
      fillColor, tmpl.fillAlpha, borderColor, 1);

    // ── Separator line under speaker name ──
    this.gfx.lineStyle(1, borderColor, 0.4);
    this.gfx.lineBetween(textOffsetX, BUBBLE_PAD + 20, bubbleW - BUBBLE_PAD, BUBBLE_PAD + 20);

    // ── Assemble container ──
    this.container.add([this.gfx, this.speakerText, this.bodyText]);

    // ── Portrait image (in-bubble mode) ──
    if (inBubblePortrait) {
      const portrait = this.scene.add.image(
        BUBBLE_PAD + portraitSize / 2, bubbleH / 2, config.portrait!,
      ).setDisplaySize(portraitSize, portraitSize).setFlipX(!!config.portraitFlip);
      const pGfx = this.scene.add.graphics();
      pGfx.lineStyle(1, borderColor, 0.5);
      pGfx.strokeRect(BUBBLE_PAD, bubbleH / 2 - portraitSize / 2, portraitSize, portraitSize);
      this.container.add([portrait, pGfx]);
    }

    // ── Standing portrait (large, at screen edge — Persona-style) ──
    if (standingPortraitSide) {
      const pc = this.scene.add.container(0, 0).setDepth(DEPTH_BUBBLE - 2);
      const scalePct = config.portraitScale ?? 0.4;
      const charOff = config.portraitOffset ?? { x: 0, y: 0 };
      const transition = config.portraitTransition ?? 'slide';

      // Fixed anchor point at bottom corner of screen
      const anchorX = standingPortraitSide === 'bottomLeft' ? 80 : CANVAS_WIDTH - 80;
      const anchorY = CANVAS_HEIGHT - 20;
      pc.setPosition(anchorX, anchorY);

      // Phone frame (behind character, fully independent)
      const useFrame = config.portraitFrame && this.scene.textures.exists('frame_smartphone');
      if (useFrame) {
        const fScale = config.frameScale ?? 0.35;
        const fOff = config.frameOffset ?? { x: 0, y: 0 };
        const phone = this.scene.add.image(fOff.x, fOff.y, 'frame_smartphone');
        const phoneH = CANVAS_HEIGHT * fScale;
        phone.setDisplaySize(phoneH * (phone.width / phone.height), phoneH);
        phone.setOrigin(0.5, 1); // anchor bottom-center
        pc.add(phone);
      }

      // Character image (on top, independent scale/offset)
      const charImg = this.scene.add.image(charOff.x, charOff.y, config.portrait!);
      const charTargetH = CANVAS_HEIGHT * scalePct;
      charImg.setDisplaySize(charTargetH * (charImg.width / charImg.height), charTargetH);
      charImg.setOrigin(0.5, 1); // anchor bottom-center
      if (config.portraitFlip) charImg.setFlipX(true);
      pc.add(charImg);

      // Entry transition
      const finalX = anchorX;
      this.entryTween?.destroy();
      this.entryTween = null;
      switch (transition) {
        case 'slide': {
          const from = standingPortraitSide === 'bottomLeft' ? finalX - 120 : finalX + 120;
          pc.x = from;
          pc.setAlpha(0);
          this.entryTween = this.scene.tweens.add({ targets: pc, x: finalX, alpha: 1, duration: 350, ease: 'Back.easeOut' });
          break;
        }
        case 'fade':
          pc.setAlpha(0);
          this.entryTween = this.scene.tweens.add({ targets: pc, alpha: 1, duration: 400, ease: 'Sine.easeOut' });
          break;
        case 'pop':
          pc.setScale(0).setAlpha(0);
          this.entryTween = this.scene.tweens.add({ targets: pc, scaleX: 1, scaleY: 1, alpha: 1, duration: 300, ease: 'Back.easeOut' });
          break;
        case 'none': break;
      }
      this.standingPortrait = pc;
    }

    // ── Position (bubblePos overrides position enum) ──
    let bx: number, by: number;
    if (config.bubblePos) {
      bx = config.bubblePos.x;
      by = config.bubblePos.y;
    } else {
      const cx = CANVAS_WIDTH / 2;
      bx = cx - bubbleW / 2;
      if (pos === 'top') {
        by = 30;
      } else if (pos === 'center') {
        by = (CANVAS_HEIGHT - bubbleH) / 2;
      } else {
        by = CANVAS_HEIGHT - bubbleH - 60;
      }
    }
    this.container.setPosition(bx, by);

    // ── Tail ──
    const tailTipScreen = this.resolveTailTip(config, bx, by, bubbleW, bubbleH);
    if (tailTipScreen && tmpl.tailStyle !== 'none') {
      this.drawTemplateTail(bx, by, bubbleW, bubbleH, tailTipScreen, tmpl, fillColor, borderColor);
    }

    // ── Fade in ──
    this.container.setAlpha(0);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1, duration: 200, ease: 'Sine.easeOut',
    });

    // ── Typewriter effect ──
    let charIdx = 0;
    const speedMult = GOD.cinematicSpeedMult ?? 1;
    this.typewriterTimer = this.scene.time.addEvent({
      delay: TYPEWRITER_CHAR_MS / speedMult,
      repeat: fullText.length - 1,
      callback: () => {
        charIdx++;
        if (this.bodyText?.active) this.bodyText.setText(fullText.substring(0, charIdx));
      },
    });

    // ── Auto-dismiss ──
    const dismissMs = config.autoDismissMs ?? (GOD.cinematicBubbleDuration ?? 3000);
    if (dismissMs > 0) {
      const totalTypewriterMs = fullText.length * (TYPEWRITER_CHAR_MS / speedMult);
      this.autoDismissTimer = this.scene.time.delayedCall(
        totalTypewriterMs + dismissMs / speedMult,
        () => this.dismiss(),
      );
    }
  }

  /** Resolve the tail tip in screen space, clamped to canvas bounds. */
  private resolveTailTip(
    config: BubbleConfig,
    bx: number, by: number, bw: number, bh: number,
  ): { x: number; y: number } | null {
    let tip: { x: number; y: number } | null = null;

    // Explicit screen-space tailTip takes priority
    if (config.tailTip) {
      tip = config.tailTip;
    } else if (config.worldAnchor) {
      // Fall back to worldAnchor projected to screen
      const cam = this.scene.cameras?.main;
      let sx = config.worldAnchor.x, sy = config.worldAnchor.y;
      if (cam) {
        const halfW = cam.width / 2;
        const halfH = cam.height / 2;
        sx = halfW + (config.worldAnchor.x - cam.scrollX - halfW) * cam.zoom;
        sy = halfH + (config.worldAnchor.y - cam.scrollY - halfH) * cam.zoom;
      }
      tip = { x: sx, y: sy };
    } else {
      // Default: 60px below bubble center (matches EditorBubbleDrag default)
      tip = { x: bx + bw / 2, y: by + bh + 60 };
    }

    // Clamp to canvas so the tail never flies off-screen
    return {
      x: Phaser.Math.Clamp(tip.x, 0, CANVAS_WIDTH),
      y: Phaser.Math.Clamp(tip.y, 0, CANVAS_HEIGHT),
    };
  }

  /** Draw the BD-style tail using shared template drawing. */
  private drawTemplateTail(
    bx: number, by: number, bw: number, bh: number,
    tip: { x: number; y: number },
    tmpl: BubbleTemplateConfig,
    fillColor: number, borderColor: number,
  ): void {
    this.tailGfx = this.scene.add.graphics().setDepth(DEPTH_BUBBLE - 1);
    // computeTailOrigin returns absolute screen coords — convert to container-local
    // since tailGfx is added to the container which is positioned at (bx, by)
    const origin = computeTailOrigin(bx, by, bw, bh, tip.x, tip.y);
    drawBubbleTail(
      this.tailGfx, origin.x - bx, origin.y - by, tip.x - bx, tip.y - by,
      tmpl, fillColor, tmpl.fillAlpha, borderColor, 1,
    );
    this.container?.add(this.tailGfx);
  }

  /** Dismiss the current bubble. Emits BUBBLE_DISMISSED unless silent.
   *  When silent=true, destroys immediately (no tween) — critical because
   *  the scene may be paused right after, which would freeze the tween
   *  and leak the container. */
  dismiss(silent = false): void {
    this.typewriterTimer?.destroy();
    this.typewriterTimer = null;
    this.autoDismissTimer?.destroy();
    this.autoDismissTimer = null;
    this.entryTween?.destroy();
    this.entryTween = null;
    this.dismissTween?.destroy();
    this.dismissTween = null;
    this.bodyText = null;

    // Clean up standing portrait
    if (this.standingPortrait) {
      if (silent) {
        this.standingPortrait.destroy();
      } else {
        this.scene.tweens.add({
          targets: this.standingPortrait,
          alpha: 0, duration: 150,
          onComplete: () => { this.standingPortrait?.destroy(); this.standingPortrait = null; },
        });
      }
      this.standingPortrait = null;
    }

    if (this.container) {
      if (silent) {
        // Immediate destroy — scene may be paused right after
        this.container.destroy();
        this.container = null;
        this.tailGfx?.destroy();
        this.tailGfx = null;
      } else {
        this.dismissTween = this.scene.tweens.add({
          targets: this.container,
          alpha: 0, duration: 150, ease: 'Sine.easeIn',
          onComplete: () => {
            this.dismissTween = null;
            this.container?.destroy();
            this.container = null;
            this.tailGfx?.destroy();
            this.tailGfx = null;
            EventBus.emit(CinematicEvents.BUBBLE_DISMISSED);
          },
        });
      }
    } else if (!silent) {
      EventBus.emit(CinematicEvents.BUBBLE_DISMISSED);
    }
  }

  /** Kill any active tweens on bubble objects (prevents frozen tweens leaking into game) */
  killTweens(): void {
    if (this.container) this.scene.tweens.killTweensOf(this.container);
    if (this.standingPortrait) this.scene.tweens.killTweensOf(this.standingPortrait);
  }

  destroy(): void {
    this.dismiss(true);
  }
}
